import { gql } from "@apollo/client";
import SaleFormInputs, { SaleType } from "components/entities/agriculture/sale/form/SaleFormInputs";
import { ComponentStateTypes } from "components/common/ComponentState";
import Page from "components/common/page/Page";
import { graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { saleTemplateMutation } from "types/agriculture/sale-types";
import { SaleTemplateFormDataQueryQuery, SaleTemplateFormDataQueryQueryVariables, SaleTemplateInput, SaleTemplateMutation, SaleTemplateMutationVariables } from "types/graphql";
import useAppContext from "utilities/hooks/useAppContext";
import useAppNavigation from "utilities/hooks/useAppNavigate";
import { numberOrUndefined } from "utilities/input-utilities";
import { DateUtil } from "utilities/date-utilities";

const saleTemplateFormDataQuery = gql`
query SaleTemplateFormDataQuery($id: Int){
    saleTemplate(id: $id) {
        name,
        season {
            name,
            id,
            active
        },
        date,
        person {
            name,
            id
        },
        fruit {
            name,
            id
        },
        kg,
        pricePerKg,
        total,
        category {
            name,
            id
        },
        cartQuantity,
        chamber,
        type,
        note
    }
}
`;

const SaleTemplateFormPage = () => {
    const navigate = useAppNavigation();
    const context = useAppContext();
    const params = useParams<{id?: string}>();
    const [componentState, setComponentState] = useState<ComponentStateTypes>(params.id ? 'loading' : '');
    const [model, setModel] = useState<SaleType>({} as SaleType);

    useEffect(() => {
        if(params.id) {
            loadData().then();
        }
    }, []);

    const loadData = async () => {
        setComponentState('loading');
        const response = await graph<SaleTemplateFormDataQueryQuery, SaleTemplateFormDataQueryQueryVariables>({
            query: saleTemplateFormDataQuery,
            variables: {
                id: Number(params.id || 0)
            }
        });
        
        if(response.errors) {
            setComponentState('error');
            console.error(response);
            return;
        }

        if(response.data?.saleTemplate) {
            let theModel = {
                ...response.data?.saleTemplate,
                id: Number(params.id || 0),
            }

            setModel(theModel as SaleType);
        }

        setComponentState('');
    }

    const onSubmit = async (model: SaleType) => {
        const saveModel: SaleTemplateInput = {
            cartQuantity: numberOrUndefined(model.cartQuantity),
            chamber: model.chamber ? true : false,
            date: model.date ? DateUtil.toIsoDate(model.date) : undefined,
            fruitId: model.fruit?.id,
            id: Number(params.id) || 0,
            kg: numberOrUndefined(model.kg),
            name: model.name,
            personId: model.person?.id,
            pricePerKg: numberOrUndefined(model.pricePerKg),
            seasonId: model.season?.id,
            total: numberOrUndefined(model.total),
            type: model.type,
            categoryId: model.fruitCategory?.id,
            note: model.note,
        }

        try {
            const response = await graph<SaleTemplateMutation, SaleTemplateMutationVariables>({
                query: saleTemplateMutation,
                variables: {
                    saleTemplate: saveModel
                }
            });

            if(response.success && response.data) {
                navigate(AppRoutes.AGRICULTURE_OVERVIEW_OTHER)
            } else {
                context.notify('error', 'form.submit.error');
                throw response.errors;
            }
        } catch (error) {
            context.notify('error', 'form.submit.error');
            throw error;
        }
    }

    return (
        <Page title="pages.template.sale.form" state={componentState}>
                <SaleFormInputs 
                    isTemplate
                    name="agriculture-sale-form-page"
                    model={model}
                    onSubmit={onSubmit}
                />
        </Page>
    );
};

export default SaleTemplateFormPage;
